import { createMiddleware } from '@tanstack/react-start';
import { getHeader } from '@tanstack/react-start/server';
import { JwksError } from 'jwks-rsa';

import { extractUserInfo, parseCookies } from '@/lib/auth';
import { COOKIE_NAMES } from '@/lib/constants/auth.constants';
import {
  ClientOptions,
  createAnalyticsApi,
  createEduBridgeApi,
  createInceptApi,
  createInternalApi,
  createOneRosterApi,
  createOneRosterApiTyped,
  createPowerpathApi,
  createQtiApi,
  createTimebackEventsApi,
} from '@/lib/http.server';

interface AccessTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface TokenCache {
  token: string;
  expiresAt: number; // Timestamp when the token expires
}

// Cache storage for the token
let tokenCache: TokenCache | null = null;

const isAuthRequired = () => !!import.meta.env.VITE_ALPHA_1EDTECH_API_IDP;

const getViewAsUserHeader = () => {
  const authCookiesHeader = getHeader('cookie');
  const cookies = parseCookies(authCookiesHeader || null);
  const idToken = cookies[COOKIE_NAMES.ID_TOKEN];
  const cognitoUser = idToken ? extractUserInfo(idToken) : undefined;
  // todo: we need to evaluate other ways to obfuscate this impersonation header
  return cognitoUser ? btoa(cognitoUser.email) : undefined;
};

const getToken = async (): Promise<AccessTokenResponse> => {
  const idp = import.meta.env.VITE_ALPHA_1EDTECH_API_IDP;
  const clientId = import.meta.env.VITE_ALPHA_1EDTECH_API_CLIENT_ID;
  const clientSecret = import.meta.env.VITE_ALPHA_1EDTECH_API_CLIENT_SECRET;

  const response = await fetch(`${idp}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      client_id: clientId,
      client_secret: clientSecret,
      grant_type: 'client_credentials',
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch token: ${response.statusText}`);
  }

  return await response.json();
};

export const requestApiAuthMiddleware = createMiddleware({
  type: 'request',
}).server(async ({ next }) => {
  const context = await createContext();
  return await next({
    context,
  });
});

export const apiAuthMiddleware = createMiddleware({ type: 'function' }).server(
  async ({ next }) => {
    const context = await createContext();
    return await next({
      context,
    });
  }
);

async function createContext() {
  if (!isAuthRequired()) {
    return {
      oneRosterApiToken: '',
      oneRosterApi: createOneRosterApi(),
      oneRosterApiTyped: createOneRosterApiTyped(),
      powerpathApi: createPowerpathApi(),
      qtiApi: createQtiApi(),
      analyticsApi: createAnalyticsApi(),
      timebackEventsApi: createTimebackEventsApi(),
      edubridgeApiTyped: createEduBridgeApi(),
      internalApiTyped: createInternalApi(),
      inceptApi: createInceptApi(),
    };
  }

  // Get the current timestamp
  const now = Date.now();
  const viewAsUserHeader =
    import.meta.env.VITE_ENABLE_VIEW_AS_USER_FILTERING === 'true'
      ? getViewAsUserHeader()
      : undefined;

  // Check if we have a valid token in cache
  if (tokenCache && tokenCache.expiresAt > now) {
    // console.log('[apiAuthMiddleware] Token is still valid, reusing it');
    // Token is still valid, reuse it
    const clientOptions: ClientOptions = {
      token: tokenCache.token,
      viewAsUserHeader,
    };
    return {
      oneRosterApiToken: tokenCache.token,
      oneRosterApi: createOneRosterApi(clientOptions),
      oneRosterApiTyped: createOneRosterApiTyped(clientOptions),
      powerpathApi: createPowerpathApi(clientOptions),
      qtiApi: createQtiApi(clientOptions),
      analyticsApi: createAnalyticsApi(clientOptions),
      timebackEventsApi: createTimebackEventsApi(clientOptions),
      edubridgeApiTyped: createEduBridgeApi(clientOptions),
      internalApiTyped: createInternalApi(clientOptions),
      inceptApi: createInceptApi(),
    };
  }

  try {
    // console.log(
    //   '[apiAuthMiddleware] No valid token in cache, fetching a new one'
    // );
    // No valid token in cache, fetch a new one
    const responseData = await getToken();

    // Calculate expiration time (subtract 5 minutes as a buffer)
    const fiveMinutes = 5 * 60 * 1000;
    const expiresInSeconds = responseData.expires_in * 1000;
    const expiresAt = now + (expiresInSeconds - fiveMinutes);

    // Update the cache
    tokenCache = {
      token: responseData.access_token,
      expiresAt,
    };

    const clientOptions: ClientOptions = {
      token: responseData.access_token,
      viewAsUserHeader,
    };

    return {
      oneRosterApiToken: responseData.access_token,
      oneRosterApi: createOneRosterApi(clientOptions),
      oneRosterApiTyped: createOneRosterApiTyped(clientOptions),
      powerpathApi: createPowerpathApi(clientOptions),
      qtiApi: createQtiApi(clientOptions),
      analyticsApi: createAnalyticsApi(clientOptions),
      timebackEventsApi: createTimebackEventsApi(clientOptions),
      edubridgeApiTyped: createEduBridgeApi(clientOptions),
      internalApiTyped: createInternalApi(clientOptions),
      inceptApi: createInceptApi(),
    };
  } catch (error) {
    if (error instanceof JwksError) {
      console.log('[apiAuthMiddleware] JwksError', error.message);
    }
    console.error('[apiAuthMiddleware] Error fetching token:');
    throw error;
  }
}
